'use client';

import { useToast } from '@/app/hooks/useToast';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProjectData } from '@/app/providers/ProjectProvider';
import { PropsWithClassname } from '@/app/types';
import { getDonations, useGetDonationsInfinite } from '@/src/api/client';

import Button, { ButtonHTMLTag } from '@/app/components/Button/Button';
import DonationRecord from '@/app/components/DonationRecord/DonationRecord';
import Loader from '@/app/components/Loader/Loader';
import { TabButton } from '@/app/components/ProjectDonorsTab/TabButton';
import SubprojectBanner from '@/app/components/SubprojectBanner/SubprojectBanner';
import {
  GetDonationsParams,
  GetDonationsSort,
  GetDonationsStatusesItem,
  GetDonationsWithVisibleComment,
  GetProjectDonationsParams,
  ProjectSettingsModeGroup,
} from '@/src/api/models';

interface ProjectDonorsTabProps extends PropsWithClassname {
  projectId: string;
  projectNumericId: number;
  isSubprojectBannerVisible?: boolean;
}

enum DonationsFilter {
  Newest = 'newest',
  Highest = 'highest',
  WithComment = 'withComment',
}

const PAGE_SIZE = 10;
const HIGHEST_DONATIONS_PAGE_SIZE = 24;

const ProjectDonorsTab = ({
  projectId,
  projectNumericId,
  isSubprojectBannerVisible,
  className,
}: ProjectDonorsTabProps) => {
  const { project } = useProjectData();
  const { t } = useTranslation();
  const { showGenericErrorToast } = useToast();
  const isPlus = project.settings.modeGroup === ProjectSettingsModeGroup.plus;

  const [filterType, setFilterType] = useState<DonationsFilter>(DonationsFilter.Newest);

  const titleTranslations: Record<DonationsFilter, string> = {
    [DonationsFilter.Newest]: t('components.ProjectDonorsTab.filters.newest'),
    [DonationsFilter.Highest]: isPlus
      ? t('components.ProjectDonorsTab.filters.highestContributions')
      : t('components.ProjectDonorsTab.filters.highestDonations'),
    [DonationsFilter.WithComment]: t('components.ProjectDonorsTab.filters.withComment'),
  };

  const limit = filterType === DonationsFilter.Highest ? HIGHEST_DONATIONS_PAGE_SIZE : PAGE_SIZE;

  const baseRequest: Omit<GetDonationsParams, 'limit' | 'offset'> = {
    project: projectId,
    statuses: [GetDonationsStatusesItem.paid],
    sort: filterType === DonationsFilter.Highest ? GetDonationsSort['amount:DESC'] : GetDonationsSort['createdAt:DESC'],
    withVisibleComment:
      filterType === DonationsFilter.WithComment ? GetDonationsWithVisibleComment.NUMBER_1 : undefined,
  };

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage, refetch, error } = useGetDonationsInfinite(
    {
      ...baseRequest,
      limit,
    },
    {
      query: {
        queryFn: async ({ pageParam = 0 }) => {
          const request: GetProjectDonationsParams = {
            ...baseRequest,
            offset: (pageParam as number) * limit,
            limit,
          };
          return getDonations(request);
        },
        queryKey: [projectId, filterType],
        initialPageParam: 0,
        getNextPageParam: (lastPage, allPages) => {
          const loadedCount = allPages.flatMap((p) => p.donations).length;
          const total = lastPage.totalItemCount ?? 0;
          return loadedCount < total ? allPages.length : undefined;
        },
      },
    },
  );

  useEffect(() => {
    if (error) {
      showGenericErrorToast();
    }
  }, [error, showGenericErrorToast, t]);

  const donations = data?.pages.flatMap((page) => page.donations) ?? [];

  const isLoadMoreAvailable = hasNextPage && filterType !== DonationsFilter.Highest;

  const handleFilterChange = (filter: DonationsFilter) => {
    setFilterType(filter);
  };

  useEffect(() => {
    refetch();
  }, [filterType]);

  return (
    <div className={classNames(className, 'flex flex-col items-center')}>
      <div className="w-max lg:w-full overflow-x-auto flex gap-[8px] justify-center lg:justify-start pb-[8px] mb-[32px]">
        {Object.values(DonationsFilter).map((filter) => (
          <TabButton
            key={filter}
            onClick={() => handleFilterChange(filter)}
            isActive={filterType === filter}
            title={titleTranslations[filter]}
          />
        ))}
      </div>

      {!donations.length && !isLoading && (
        <div>
          <div className="my-[20px] mb-[140px]">{t('components.ProjectDonorsTab.noDonationsYet')}</div>
          {isSubprojectBannerVisible && <SubprojectBanner projectNumericId={projectNumericId} className="mt-[40px]" />}
        </div>
      )}

      {!!donations.length && (
        <div>
          {donations.map((donation) => (
            <DonationRecord
              key={donation.id}
              id={donation.id!}
              username={donation.donorName!}
              date={new Date(donation.createdAt)}
              message={donation.isCommentVisible && !!donation.donorComment ? donation.donorComment : undefined}
              amountInCents={donation.amountInCents!}
            />
          ))}
        </div>
      )}
      {(isLoading || isFetchingNextPage) && <Loader />}

      {isLoadMoreAvailable && (
        <Button
          testId="load-more-donations-button"
          className="self-center px-[24px] py-[20px]"
          tagSpecificProps={{
            htmlTag: ButtonHTMLTag.BUTTON,
            onClick: () => fetchNextPage(),
          }}
        >
          {t('components.ProjectDonorsTab.moreDonations')}
        </Button>
      )}

      {isSubprojectBannerVisible && <SubprojectBanner projectNumericId={projectNumericId} className="mt-[40px]" />}
    </div>
  );
};

export default ProjectDonorsTab;
